import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import useFileDrop from '@/hooks/useFileDrop';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import {
  Files, MagnifyingGlass, UploadSimple, Folder, FolderOpen, Archive, Trash,
  Plus, Tray, SquaresFour, ListBullets, Columns, House, FileDashed, BellRinging,
  GearSix, X, PushPin, ClockCounterClockwise, Books, QrCode, Sparkle, Printer, LockKey,
  ChatCircleText, TreeStructure,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { dmsApi, SIMPLE_STATUS_STYLE, SIMPLE_STATUS_OPTIONS, fmtDate, fmtSize } from './dmsApi';
import DocUploadDialog from './DocUploadDialog';
import DocDetailDrawer from './DocDetailDrawer';
import DocViewer from './DocViewer';
import DocRequests from './DocRequests';
import FolderDialog from './FolderDialog';
import DmsAdminPanel from './DmsAdminPanel';
import BundlesView, { CreateBundleDialog } from './BundlesView';
import ScanInboxView from './ScanInboxView';
import { BarcodeScanDialog, AssetDocsView } from './BarcodeDocs';
import AskScopeDialog from './AskScopeDialog';
import DocCard from './DocCard';
import DocReadDialog from './DocReadDialog';
import FolderTreePanel from './FolderTreePanel';
import MixedDocNotice from './MixedDocNotice';
import FolderBar, { FolderBreadcrumb } from './FolderBar';
import MixedReviewList from './MixedReviewList';
import DuplicateCheckDialog from './DuplicateCheckDialog';
import ShareMenu from './ShareMenu';
import DocsHealthCard from './DocsHealthCard';
import ConfirmDialog from './ConfirmDialog';
import { DangerZone, DangerAction } from '@/components/DangerZone';
import { useAuth } from '@/auth/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const _authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function TrashDangerZone({ onEmptied }) {
  const preview = async () => {
    const r = await axios.post(`${API}/api/dms/trash/empty-preview`, {},
      { headers: _authHeader() });
    return { affected: r.data?.affected || 0, unit: '件', unrecoverable: true,
             detailJa: 'ファイル本体・関連付け・検索索引もすべて削除されます' };
  };
  const confirmEmpty = async () => {
    const r = await axios.post(`${API}/api/dms/trash/empty`,
      { confirm: true, reason: 'admin_dms_trash_empty_from_dms_desk' },
      { headers: _authHeader() });
    if (onEmptied) setTimeout(onEmptied, 300);
    return { ok: true, deleted: r.data?.deleted || 0, message_ja: r.data?.message_ja };
  };
  return (
    <DangerZone titleJa="ゴミ箱を空にする"
      descriptionJa="この操作は取り消せません。書類のファイル本体、関連付け、検索索引を完全に削除します。"
      testid="danger-dms-trash">
      <DangerAction
        testid="danger-dms-trash-action"
        titleJa="ゴミ箱の全書類を完全に削除"
        consequenceJa="ゴミ箱にある全ての書類を、ファイル本体と共に完全に削除します。バックアップからしか復元できません。"
        previewButton={{ labelJa: '影響を確認', onPreview: preview }}
        confirmPromptJa="ゴミ箱を空にする"
        onConfirm={confirmEmpty}
      />
    </DangerZone>
  );
}

import { AccessBadge, fileIcon } from './docsUi';
import { stashFiles } from './ingestQueue';

const PAGE_TITLE = {
  home: '最近の書類',
  browse: 'すべての書類',
  scan: 'スキャナー受信箱',
  requests: '未提出書類',
  bundles: 'バインダー',
  trash: 'ゴミ箱',
  admin: '設定・管理',
  assetdocs: '関連する書類',
  mixed: '複数書類の確認',
};

export default function DocumentDesk() {
  const { user } = useAuth();
  const _isAdmin = (user?.role || '') === 'admin';
  const [meta, setMeta] = useState(null);
  const [folders, setFolders] = useState([]);
  const [trays, setTrays] = useState([]);
  const [reminders, setReminders] = useState(null);
  const [prefs, setPrefs] = useState({ pinned_folder_ids: [], recent_folder_ids: [] });
  const [docs, setDocs] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('home');
  const [folderId, setFolderId] = useState(null);
  const [trayId, setTrayId] = useState(null);
  const [q, setQ] = useState('');
  const [slow, setSlow] = useState(false);
  // 打っている最中に毎文字検索を投げない（300ms待つ）。前の検索は打ち切る。
  const qd = useDebouncedValue(q.trim(), 300);
  const [statusF, setStatusF] = useState('');
  const [folderQ, setFolderQ] = useState('');
  const [typeF, setTypeF] = useState('');
  const [mode, setMode] = useState(() => localStorage.getItem('dms_mode') || 'grid');
  const [cardSize, setCardSize] = useState(() => localStorage.getItem('dms_card_size') || 'md');
  const [selected, setSelected] = useState({});
  const [shareFor, setShareFor] = useState(null);   // {ids} | {bundleId, label}
  const [uploadOpen, setUploadOpen] = useState(false);
  const [folderDlg, setFolderDlg] = useState(null);
  const [viewerDocId, setViewerDocId] = useState(null);
  const [viewerPage, setViewerPage] = useState(null);
  const [editorDocId, setEditorDocId] = useState(null);
  const [bundleCtx, setBundleCtx] = useState(null);
  const [bundlePick, setBundlePick] = useState(null);
  const [scanDlg, setScanDlg] = useState(false);
  const [assetTarget, setAssetTarget] = useState(null);
  const [initialBundleId, setInitialBundleId] = useState(null);
  const [askScopeOpen, setAskScopeOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [folderNavOpen, setFolderNavOpen] = useState(false);
  const [folderPinned, setFolderPinned] = useState(() => localStorage.getItem('dms_folder_pinned') === '1');
  const [pillKey, setPillKey] = useState(0);
  // U2 — 探す・聞くが4箇所に散っていた。検索欄ひとつに「キーワード / AI質問 / NAS」を持たせる。
  const [searchMode, setSearchMode] = useState('keyword');
  // D7 — まとめて片付ける（ゴミ箱へ / 移動 / 種類変更）。確認してから実行する。
  const [confirm, setConfirm] = useState(null);
  // L8 — カードの ⋯ から読み直し・修正を開く
  const [readDlg, setReadDlg] = useState(null);
  // L5 — 表示件数（50件ずつ）
  const [shown, setShown] = useState(50);
  const [bulkMode, setBulkMode] = useState(null);   // 'move' | 'type' | null
  const fileInputRef = useRef(null);
  const [dupState, setDupState] = useState(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  const setModeP = (m) => { setMode(m); localStorage.setItem('dms_mode', m); };
  const setCardSizeP = (s) => { setCardSize(s); localStorage.setItem('dms_card_size', s); };

  const loadBase = useCallback(async () => {
    try {
      const m = await dmsApi.meta();
      setMeta(m);
      const [f, t, r, rem, p] = await Promise.all([
        dmsApi.folders(),
        m.features?.tray_workflow ? dmsApi.trays().catch(() => []) : Promise.resolve([]),
        dmsApi.documents({ limit: 10 }).catch(() => []),
        dmsApi.reminders().catch(() => null),
        dmsApi.prefs().catch(() => ({ pinned_folder_ids: [], recent_folder_ids: [] })),
      ]);
      setFolders(f); setTrays(t); setRecent(r); setReminders(rem); setPrefs(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ドキュメントの読み込みに失敗しました');
    }
  }, []);

  const loadDocs = useCallback(async () => {
    if (['home', 'admin', 'requests', 'bundles', 'scan', 'assetdocs', 'mixed'].includes(view)) return;
    setLoading(true);
    setSlow(false);
    const slowTimer = setTimeout(() => setSlow(true), 10000);
    try {
      const params = { deleted: view === 'trash' };
      if (view === 'browse') {
        if (folderId) params.folder_id = folderId;
        if (trayId) params.tray = trayId;
      }
      if (qd) params.q = qd;
      if (statusF === 'active') { params.archived = 'exclude'; params.include_obsolete = false; }
      else if (statusF === 'obsolete') { params.status = 'obsolete'; }
      else if (statusF === 'archived') { params.archived = 'only'; }
      if (typeF) params.doc_type = typeF;
      setDocs(await dmsApi.documentsLatest(params));
    } catch (e) {
      if (dmsApi.isCanceled(e)) return;     // 新しい検索が始まっただけ
      if (e?.response?.status !== 403) toast.error(e?.response?.data?.detail || '書類の取得に失敗しました');
      setDocs([]);
    } finally {
      clearTimeout(slowTimer);
      setLoading(false);
      setSlow(false);
    }
  }, [view, folderId, trayId, qd, statusF, typeF]);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { loadDocs(); }, [loadDocs]);
  useEffect(() => { setShown(50); }, [view, folderId, trayId, qd, statusF, typeF]);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    // U5 — 資産台帳のラベル読取から ?view=assetdocs&asset_type=..&asset_id=.. で来る
    const at = searchParams.get('asset_type');
    const ai = searchParams.get('asset_id');
    if (at && ai) { setAssetTarget({ ttype: at, tid: ai }); setView('assetdocs'); }
    const openId = searchParams.get('open');
    const bundleId = searchParams.get('bundle');
    const page = searchParams.get('page');
    if (openId || bundleId) {
      if (openId) { setViewerDocId(openId); setViewerPage(page ? Number(page) : null); }
      if (bundleId) { setInitialBundleId(bundleId); setView('bundles'); }
      const next = new URLSearchParams(searchParams);
      next.delete('open'); next.delete('bundle'); next.delete('page');
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // UX-2.5A · MODEL A — the Documents ModuleSidebar owns navigation and drives
  // the workspace through ?view=. Folder state is deliberately NOT reset here,
  // so opening/closing the folder drawer or returning from a document keeps the
  // current folder and list.
  const urlView = searchParams.get('view');
  useEffect(() => {
    if (!urlView || urlView === view) return;
    setView(urlView);
    setSelected({});
    if (urlView !== 'browse') { setTrayId(null); }
  }, [urlView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (q.trim() && view === 'home') setView('browse'); }, [q, view]);

  // ⌘K / Ctrl+K で検索へ（探すが主役）
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault(); searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const tree = useMemo(() => ({
    roots: folders.filter((f) => !f.parent_folder_id),
    children: (pid) => folders.filter((f) => f.parent_folder_id === pid),
  }), [folders]);

  const refreshAll = () => { loadBase(); loadDocs(); setPillKey((k) => k + 1); };
  const goHome = () => { setView('home'); setFolderId(null); setTrayId(null); setQ(''); setSelected({}); };
  const openTray = (tid) => {
    if (tid === 'scan_inbox' && meta?.features?.scanner_inbox) { setView('scan'); setFolderId(null); setTrayId(null); setSelected({}); return; }
    setView('browse'); setTrayId(tid); setFolderId(null); setSelected({});
  };
  const openFolder = (fid) => {
    // U4 — スキャナー受信箱はサイドバーの画面が本体。木からも同じ画面へ送る。
    const f = folders.find((x) => x.id === fid);
    if (f && (f.seed_key === 'scan_inbox' || f.name === 'スキャナー受信箱')
        && meta?.features?.scanner_inbox) {
      setView('scan'); setFolderId(null); setTrayId(null); setSelected({}); return;
    }
    setView('browse'); setFolderId(fid); setTrayId(null); setSelected({});
    if (fid) dmsApi.visitFolder(fid).then((r) => setPrefs((p) => ({ ...p, ...r }))).catch(() => {});
  };

  // ── 取り込み: どこにドロップしても受け付ける。開いているフォルダーが保存先 ──
  // 取り込みの前に「同じファイルが既にないか」を必ず照合する（D1/D2 の再利用）。
  const sha256Hex = async (file) => {
    if (!window.crypto?.subtle || file.size > 80 * 1024 * 1024) return null;
    try {
      const buf = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
      return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch { return null; }
  };

  const beginIngest = async (files) => {
    try {
      const batch = await dmsApi.ingestCreateBatch(folderId || null, files.length);
      stashFiles(batch.id, files);
      navigate(`/documents/ingest/${batch.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '取り込みを開始できませんでした');
    }
  };

  const startIngest = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 200);
    if (!files.length) return;
    try {
      const payload = await Promise.all(files.map(async (f) => ({
        filename: f.name, size: f.size, sha256: await sha256Hex(f),
        folder_id: folderId || null,
      })));
      const res = await dmsApi.uploadCheckBatch(payload);
      if (res?.with_duplicates) { setDupState({ res, files }); return; }
    } catch (_e) { /* 照合できなくても取り込みは止めない */ }
    await beginIngest(files);
  };

  const resolveDuplicates = async (decisions) => {
    const state = dupState;
    setDupState(null);
    if (!state) return;
    const byName = Object.fromEntries(decisions.map((d) => [d.filename, d]));
    const open = decisions.find((d) => d.choice === 'open_existing');
    if (open) { setViewerDocId(open.existing.document_id); }
    const moves = decisions.filter((d) => d.choice === 'move_existing');
    for (const m of moves) {
      const from = m.existing.folder_id;
      try {
        await dmsApi.bulkAction({ action: 'move', document_ids: [m.existing.document_id],
          folder_id: folderId });
        toast.success(`${m.existing.document_number || m.existing.title} をこのフォルダへ移動しました`, {
          action: { label: '元に戻す',
            onClick: () => dmsApi.bulkAction({ action: 'move',
              document_ids: [m.existing.document_id], folder_id: from })
              .then(() => { toast.success('移動を元に戻しました'); loadBase(); }) },
        });
      } catch (e) { toast.error(e?.response?.data?.detail || '移動できませんでした'); }
    }
    const keep = state.files.filter((f) => {
      const d = byName[f.name];
      return !d || d.choice === 'separate';
    });
    const reasons = decisions.filter((d) => d.choice === 'separate' && d.reason)
      .map((d) => `${d.filename}: ${d.reason}`);
    if (reasons.length) {
      try { await dmsApi.logDuplicateAck(reasons); } catch (_e) { /* 記録だけ */ }
    }
    if (keep.length) await beginIngest(keep);
    else { loadBase(); }
  };

  // ドラッグ＆ドロップはウィンドウ全体で受け取る。ページの一部だけに
  // ハンドラを付けていたため、余白やヘッダーに落とすとブラウザがPDFを
  // 開いてしまい取り込まれない不具合があった（iter_294で修正）。
  const { dragging } = useFileDrop(!!meta?.can_edit, startIngest);

  const moveSelected = async (targetFolderId) => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) { toast.error('移動する書類を選択してください'); return; }
    let ok = 0;
    for (const id of ids) {
      const d = docs.find((x) => x.id === id);
      if (!d) continue;
      try {
        await dmsApi.updateDocument(id, {
          title: d.title, description: d.description || null, folder_id: targetFolderId,
          doc_type: d.doc_type, status: d.status, confidentiality: d.confidentiality, tags: d.tags || [],
        });
        ok += 1;
      } catch (e) { toast.error(`${d.title}: ${e?.response?.data?.detail || '移動失敗'}`); }
    }
    if (ok) toast.success(`${ok}件をフォルダーへ移動しました`);
    setSelected({});
    refreshAll();
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const runBulk = async (action, extra) => {
    const ids = extra?.document_ids || selectedIds;
    if (!ids.length) { toast.error('書類を選択してください'); return; }
    try {
      const r = await dmsApi.bulk({ action, document_ids: ids, ...extra });
      if (r.succeeded) toast.success(`${r.succeeded}件を処理しました`);
      if (r.failed) toast.error(`${r.failed}件は処理できませんでした（${r.failures[0]?.reason_ja || ''}）`);
      setSelected({}); setBulkMode(null); refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '処理に失敗しました');
    }
  };

  // D7 — カードごとの ⋯。削除は必ず確認を挟む。
  const cardActions = (d) => (meta.can_edit ? [
    { key: 'reread', label: '読み直す・修正', onClick: (doc) => setReadDlg(doc) },
    { key: 'move', label: 'フォルダーを移動', onClick: (doc) => { setSelected({ [doc.id]: true }); setBulkMode('move'); } },
    { key: 'type', label: '種類を変更', onClick: (doc) => { setSelected({ [doc.id]: true }); setBulkMode('type'); } },
    { key: 'trash', label: 'ゴミ箱へ', tone: 'danger', onClick: (doc) => setConfirm({
      title: 'ゴミ箱へ移動', message: `「${doc.title}」をゴミ箱へ移動します。\nゴミ箱からは保持期間内であれば戻せます。`,
      confirmLabel: 'ゴミ箱へ移動', tone: 'danger',
      onConfirm: () => { setConfirm(null); runBulk('trash', { document_ids: [doc.id] }); } }) },
  ] : []);

  if (!meta) return <p className="py-20 text-center text-sm text-zinc-400">読み込み中...</p>;

  const canDrop = !!meta.can_edit;
  const currentFolder = folders.find((f) => f.id === folderId) || null;
  const pinnedFolders = (prefs.pinned_folder_ids || [])
    .map((id) => folders.find((f) => f.id === id)).filter(Boolean);
  const recentFolders = (prefs.recent_folder_ids || [])
    .map((id) => folders.find((f) => f.id === id)).filter(Boolean).slice(0, 6);

  const FolderRow = ({ f, depth }) => (
    <button type="button" onClick={() => openFolder(f.id)}
      data-testid={`dms-folder-${f.seed_key || f.id}`}
      className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm transition-colors duration-200 text-left ${
        folderId === f.id && view === 'browse'
          ? 'bg-teal-50 text-teal-900 font-semibold shadow-[inset_3px_0_0_0_theme(colors.teal.700)]'
          : 'text-zinc-700 hover:bg-white'}`}
      style={{ paddingLeft: 8 + depth * 14 }}>
      {f.is_archive_folder
        ? <Archive size={14} className="text-zinc-400 shrink-0" />
        : folderId === f.id
          ? <FolderOpen size={14} weight="duotone" className="text-teal-600 shrink-0" />
          : <Folder size={14} className="text-zinc-300 shrink-0" />}
      <span className="truncate flex-1">{f.name}</span>
      {(prefs.pinned_folder_ids || []).includes(f.id) && <PushPin size={10} weight="fill" className="text-teal-600" />}
    </button>
  );
  const renderTree = (list, depth) => list.map((f) => (
    <div key={f.id}><FolderRow f={f} depth={depth} />{renderTree(tree.children(f.id), depth + 1)}</div>
  ));

  const NavItem = ({ icon: Icon, label, active, onClick, testId, badge }) => (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left transition-colors duration-200 ${
        active
          ? 'bg-teal-50 text-teal-900 font-semibold shadow-[inset_3px_0_0_0_theme(colors.teal.700)]'
          : 'text-zinc-700 hover:bg-white'}`}>
      <Icon size={15} className="text-zinc-400" />
      <span className="flex-1 truncate">{label}</span>
      {badge > 0 && <span className="text-xs font-semibold text-violet-600 tabular-nums">{badge}</span>}
    </button>
  );

  // L5 — 1画面に300件並べるとサムネイルの要求が一気に出る。50件ずつ出す。
  const PAGE_SIZE = 50;
  const visibleDocs = docs.slice(0, shown);
  const MoreButton = () => (docs.length > shown ? (
    <div className="mt-4 flex justify-center">
      <Button variant="outline" onClick={() => setShown((n) => n + PAGE_SIZE)}
        data-testid="dms-load-more" className="h-9 text-sm">
        もっと見る（残り {docs.length - shown}件）
      </Button>
    </div>
  ) : null);

  const DocList = () => (
    <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden" data-testid="dms-doc-list">
      <table className="w-full text-sm">
        <thead className="bg-zinc-100 text-zinc-700 text-xs">
          <tr>
            <th className="w-9 px-2 py-2">
              <input type="checkbox" aria-label="表示中のすべてを選択"
                data-testid="dms-list-select-all" className="h-4 w-4 accent-teal-600"
                checked={docs.length > 0 && docs.every((d) => selected[d.id])}
                onChange={(e) => setSelected(e.target.checked
                  ? Object.fromEntries(docs.map((d) => [d.id, true])) : {})} />
            </th>
            {/* 列幅の規則は DataTable と同じ: 内容の列（タイトル）が残り幅を取り、
                固定書式の列は内容ぴったり。ここは共通コンポーネントを通らないので個別に当てる。 */}
            <th className="text-left px-3 py-2 font-semibold" style={{ width: '100%' }}>タイトル</th>
            <th className="text-left px-2 py-2 font-semibold whitespace-nowrap w-32">番号</th>
            <th className="text-left px-2 py-2 font-semibold whitespace-nowrap w-24">種別</th>
            <th className="text-left px-2 py-2 font-semibold whitespace-nowrap w-28">状態</th>
            <th className="text-left px-2 py-2 font-semibold whitespace-nowrap w-28 hidden lg:table-cell">所有者</th>
            <th className="text-left px-2 py-2 font-semibold whitespace-nowrap w-28 hidden lg:table-cell">更新</th>
            <th className="text-right px-3 py-2 font-semibold whitespace-nowrap w-24 hidden sm:table-cell">サイズ</th>
          </tr>
        </thead>
        <tbody>
          {visibleDocs.map((d) => (
            <tr key={d.id} onClick={() => setViewerDocId(d.id)}
              className="border-t border-zinc-200 hover:bg-teal-50/40 cursor-pointer"
              data-testid={`dms-doc-row-${d.id}`}>
              <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" className="h-4 w-4 accent-teal-600"
                  aria-label={`${d.title} を選択`} data-testid={`dms-list-select-${d.id}`}
                  checked={!!selected[d.id]}
                  onChange={(e) => setSelected((p) => ({ ...p, [d.id]: e.target.checked }))} />
              </td>
              <td className="max-w-0 px-3 py-2 font-medium text-zinc-800" style={{ width: '100%' }}>
                <span className="flex items-center gap-1.5 min-w-0">
                  {d.preview_allowed === false ? <LockKey size={13} className="shrink-0 text-zinc-300" /> : fileIcon(d, 15)}
                  <span className="min-w-0 line-clamp-2 break-words"
                    title={d.preview_allowed === false ? '（保護された書類）' : d.title}>
                    {d.preview_allowed === false ? '（保護された書類）' : <span data-no-translate>{d.title}</span>}
                  </span>
                </span>
                {d.content_snippet && (
                  <span className="block text-xs text-zinc-500 truncate font-normal" title={d.content_snippet}>{d.content_snippet}</span>
                )}
              </td>
              <td className="px-2 py-2 font-mono text-zinc-400 text-xs whitespace-nowrap">{d.document_number}</td>
              <td className="px-2 py-2 text-zinc-500 whitespace-nowrap">{d.doc_type_label}</td>
              <td className="px-2 py-2 whitespace-nowrap"><span className={`inline-block whitespace-nowrap px-1.5 py-0.5 rounded border text-xs font-medium ${SIMPLE_STATUS_STYLE[d.status_simple] || ''}`}>{d.status_simple_label}</span></td>
              <td className="px-2 py-2 text-zinc-500 whitespace-nowrap hidden lg:table-cell"><span data-no-translate>{d.owner_name}</span></td>
              <td className="px-2 py-2 text-zinc-400 whitespace-nowrap hidden lg:table-cell">{fmtDate(d.updated_at)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-zinc-400 whitespace-nowrap hidden sm:table-cell">{fmtSize(d.file_size)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="max-w-[1600px] mx-auto relative" data-testid="dms-desk">
      {dragging && canDrop && (
        <div className="fixed inset-0 z-50 bg-teal-50/80 backdrop-blur-sm border-4 border-dashed border-teal-500 flex flex-col items-center justify-center pointer-events-none"
          data-testid="dms-drop-overlay">
          <UploadSimple size={40} weight="duotone" className="text-teal-700 mb-3" />
          <p className="text-xl font-semibold text-teal-900">
            {currentFolder ? `「${currentFolder.name}」に取り込みます` : 'ここにドロップして取り込みます'}
          </p>
          <p className="text-base text-teal-700 mt-1">
            まとめてドロップできます。AIが内容を読み取り、確認が必要なものだけお知らせします
          </p>
        </div>
      )}

      {/* 見出し — 現在いるフォルダーが主役。上の行が現在位置とフォルダーの開閉を担う */}
      <PageHeader
        title={view === 'browse' && currentFolder ? currentFolder.name : (PAGE_TITLE[view] || '最近の書類')}
        eyebrow={view === 'browse' ? (
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={() => setFolderNavOpen(true)}
              data-testid="dms-folder-nav-btn"
              aria-label="フォルダーを開く" aria-expanded={folderNavOpen}
              className={`inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2.5 min-h-[32px] text-sm text-zinc-700 hover:bg-zinc-50 transition-colors focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none${folderPinned ? ' xl:hidden' : ''}`}>
              <TreeStructure size={15} /> フォルダー
            </button>
            {currentFolder && (
              <FolderBreadcrumb folder={currentFolder} folders={folders} onOpenFolder={openFolder} />
            )}
          </div>
        ) : undefined}
        description={view === 'home' ? '最近使用した書類と、よく使う場所を確認できます。' : undefined}
        testid="dms-page-header"
        className="mb-4"
        /* U2「まとめて質問」は検索欄のAI質問に統合。
           U5「資産ラベル読取」は資産台帳へ移設（ここからは外す）。 */
        menuItems={[
          ...(meta.features?.tray_workflow ? trays.map((t) => ({
            label: `トレイ: ${t.name}${t.count ? `（${t.count}）` : ''}`,
            testid: `dms-menu-tray-${t.id}`, onClick: () => openTray(t.id),
          })) : []),
        ]}
        secondary={(
          <>
            {/* U1 — フォルダーを開いているときは、フォルダーの道具箱側の1つだけにする */}
            {meta.can_edit && !(view === 'browse' && currentFolder) && (
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}
                className="h-10 text-sm border-zinc-200" data-testid="dms-ingest-btn">
                <UploadSimple size={15} className="mr-1.5" /> ファイルを追加
              </Button>
            )}
            <div className="flex rounded-md border border-zinc-200 overflow-hidden" data-testid="dms-mode-switch">
              {[['grid', SquaresFour, 'サムネイル表示'], ['list', ListBullets, 'リスト表示'], ['split', Columns, '分割表示']].map(([m, IconC, label]) => (
                <button key={m} type="button" onClick={() => setModeP(m)} title={label}
                  aria-label={label} aria-pressed={mode === m}
                  data-testid={`dms-mode-${m}`}
                  className={`h-10 w-10 flex items-center justify-center transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none ${mode === m ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-500 hover:bg-zinc-50'}`}>
                  <IconC size={15} weight={mode === m ? 'fill' : 'regular'} />
                </button>
              ))}
            </div>
            {mode === 'grid' && view !== 'bundles' && (
              <div className="flex rounded-md border border-zinc-200 overflow-hidden" data-testid="dms-card-size-switch">
                {[['sm', '小'], ['md', '中'], ['lg', '大']].map(([s, label]) => (
                  <button key={s} type="button" onClick={() => setCardSizeP(s)}
                    title={`カードの大きさ: ${label}`} aria-pressed={cardSize === s}
                    data-testid={`dms-card-size-${s}`}
                    className={`h-10 w-9 text-xs font-semibold transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none ${
                      cardSize === s ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-500 hover:bg-zinc-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            {meta.is_admin && (
              <Button variant="ghost" onClick={() => setAdminOpen(true)}
                className="h-10 w-10 p-0 text-zinc-500"
                title="ドキュメントの設定・管理" aria-label="ドキュメントの設定・管理"
                data-testid="dms-admin-drawer-btn">
                <GearSix size={18} weight="duotone" />
              </Button>
            )}
          </>
        )}
      />
      {meta.can_edit && (
        <>
          <DuplicateCheckDialog open={!!dupState} result={dupState?.res}
            folderName={currentFolder?.name}
            onCancel={() => { setDupState(null); toast.message('取り込みをやめました（何も登録していません）'); }}
            onProceed={resolveDuplicates} />
          <ShareMenu open={!!shareFor} onClose={() => setShareFor(null)}
            documentIds={shareFor?.ids || []} bundleId={shareFor?.bundleId || null}
            label={shareFor?.label || ''} isAdmin={!!meta?.is_admin} />
          <input ref={fileInputRef} type="file" multiple className="hidden"
            data-testid="dms-ingest-file-input"
            onChange={(e) => { startIngest(e.target.files); e.target.value = ''; }} />
        </>
      )}

      {/* 主アクション: 探す */}
      {meta.features?.search && (
        <div className="mb-7 max-w-[960px]" data-testid="dms-search-hero">
          <div className="mb-2 inline-flex rounded-full border border-zinc-200 bg-white p-0.5" data-testid="dms-search-modes">
            {[['keyword', 'キーワード'], ...(meta.features?.ai_assist ? [['ask', 'AI質問']] : []), ['nas', 'NAS']].map(([m, label]) => (
              <button key={m} type="button" onClick={() => setSearchMode(m)} aria-pressed={searchMode === m}
                data-testid={`dms-search-mode-${m}`}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  searchMode === m ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:text-zinc-800'}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="relative">
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                if (searchMode === 'ask') { setAskScopeOpen(true); }
                if (searchMode === 'nas') { navigate(`/documents/nas${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`); }
              }}
              placeholder={currentFolder
                ? `「${currentFolder.name}」内を探す — ファイル名・書類番号・書類の中身`
                : '書類を探す — ファイル名・書類番号・書類の中身・プロジェクト・設備・資産'}
              className="h-14 pl-12 pr-20 text-base rounded-xl border-transparent bg-white shadow-md ring-1 ring-zinc-200 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:shadow-lg transition-[box-shadow] duration-200"
              data-testid="dms-search-input" />
            {q ? (
              <button type="button" onClick={() => { setQ(''); goHome(); }} data-testid="dms-search-clear"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
                <X size={16} />
              </button>
            ) : (
              <span className="absolute right-4 top-1/2 -translate-y-1/2 rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-xs text-zinc-400">⌘K</span>
            )}
          </div>
          {(q || view === 'browse') && (
            <div className="flex items-center gap-2 flex-wrap mt-3" data-testid="dms-filters">
              <Select value={typeF || 'all'} onValueChange={(v) => setTypeF(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-auto min-w-[120px] text-sm rounded-full border-zinc-200 bg-white"
                  data-testid="dms-filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全種別</SelectItem>
                  {meta.doc_types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusF || 'all'} onValueChange={(v) => setStatusF(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-auto min-w-[130px] text-sm rounded-full border-zinc-200 bg-white"
                  data-testid="dms-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SIMPLE_STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value || 'all'} value={s.value || 'all'}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {trayId && (
                <span className="rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 text-sm text-teal-800"
                  data-testid="dms-tray-label">
                  トレイ: {(trays.find((t) => t.id === trayId) || {}).name}
                </span>
              )}
              {view === 'trash' && (
                <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-sm text-rose-700" data-testid="dms-trash-note">
                  ゴミ箱 — 保持期間内であれば復元できます
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-6">
        {/* フォルダーの場所ナビゲーション（コンテンツ側 — 既定は重ねて開くパネル） */}
        {view === 'browse' && (
          <FolderTreePanel roots={tree.roots} childrenOf={tree.children} folderId={folderId}
            canManage={meta.can_manage} onOpenFolder={openFolder}
            pinned={folderPinned} open={folderNavOpen}
            onClose={() => setFolderNavOpen(false)}
            onTogglePin={() => setFolderPinned((v) => {
              const next = !v;
              localStorage.setItem('dms_folder_pinned', next ? '1' : '0');
              if (next) setFolderNavOpen(false);
              return next;
            })}
            onNewFolder={() => setFolderDlg({})} />
        )}
        {/* 本体 */}
        <main className="flex-1 min-w-0" data-testid="dms-main">
          {view === 'admin' ? (
            <div className="space-y-4"><DocsHealthCard /><DmsAdminPanel /></div>
          ) : view === 'requests' ? (
            <DocRequests meta={meta} folders={folders} onChanged={loadBase} />
          ) : view === 'bundles' ? (
            <BundlesView meta={meta} initialBundleId={initialBundleId}
              onOpenDoc={(id, ctx) => { setBundleCtx(ctx || null); setViewerDocId(id); }} />
          ) : view === 'scan' ? (
            <ScanInboxView meta={meta} folders={folders}
              onOpenDoc={(id) => setViewerDocId(id)} onChanged={loadBase} />
          ) : view === 'mixed' ? (
            <MixedReviewList onOpenDoc={(id) => setViewerDocId(id)} />
          ) : view === 'assetdocs' ? (
            <AssetDocsView target={assetTarget} onBack={goHome}
              onOpenDoc={(id) => setViewerDocId(id)} />
          ) : view === 'home' ? (
            <div className="space-y-8" data-testid="dms-home">
              {meta?.features?.document_requests && reminders && (reminders.counts?.overdue_requests > 0 || reminders.counts?.due_soon_requests > 0) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3 flex items-start gap-2.5"
                  data-testid="dms-reminders-banner">
                  <BellRinging size={18} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900 space-y-0.5">
                    <p className="font-semibold">お知らせ</p>
                    {reminders.counts.overdue_requests > 0 && (
                      <button type="button" className="block underline" onClick={() => setView('requests')}
                        data-testid="dms-reminder-overdue">
                        期限を過ぎた書類リクエストが {reminders.counts.overdue_requests}件 あります
                      </button>
                    )}
                    {reminders.counts.due_soon_requests > 0 && (
                      <button type="button" className="block underline" onClick={() => setView('requests')}
                        data-testid="dms-reminder-due-soon">
                        3日以内に期限を迎えるリクエストが {reminders.counts.due_soon_requests}件 あります
                      </button>
                    )}
                  </div>
                </div>
              )}

              <section data-testid="dms-pinned">
                <div className="flex items-center gap-2 mb-3">
                  <PushPin size={15} weight="duotone" className="text-teal-700" />
                  <p className="text-base font-medium text-zinc-800">ピン留めしたフォルダー</p>
                </div>
                {pinnedFolders.length === 0 ? (
                  <p className="text-sm text-zinc-400">よく使うフォルダーを開いて「ピン留め」すると、ここに並びます</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {pinnedFolders.map((f) => (
                      <button key={f.id} type="button" onClick={() => openFolder(f.id)}
                        data-testid={`dms-pinned-${f.id}`}
                        className="text-left rounded-lg border border-zinc-200 bg-white p-4 hover:border-teal-300 hover:shadow-md transition-[box-shadow,border-color] duration-200">
                        <FolderOpen size={22} weight="duotone" className="text-teal-700 mb-2" />
                        <p className="text-base font-medium text-zinc-900 truncate">{f.name}</p>
                        <p className="text-sm text-zinc-500 tabular-nums">
                          {f.document_count_total ?? f.document_count ?? 0}件
                          {(f.document_count_total ?? 0) > (f.document_count ?? 0) ? '（サブ含む）' : ''}
                        </p>
                        <span className="mt-1 inline-flex"><AccessBadge folder={f} /></span>
                      </button>
                    ))}
                  </div>
                )}
              </section>

              {recentFolders.length > 0 && (
                <section data-testid="dms-recent-folders">
                  <div className="flex items-center gap-2 mb-3">
                    <ClockCounterClockwise size={15} weight="duotone" className="text-zinc-500" />
                    <p className="text-base font-medium text-zinc-800">最近開いたフォルダー</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {recentFolders.map((f) => (
                      <button key={f.id} type="button" onClick={() => openFolder(f.id)}
                        data-testid={`dms-recent-folder-${f.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-700 hover:border-teal-300 hover:text-teal-800 transition-colors duration-200">
                        <Folder size={13} className="text-zinc-400" />{f.name}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              <section data-testid="dms-recent">
                <p className="text-base font-medium text-zinc-800 mb-3">最近の書類</p>
                {recent.length === 0 ? (
                  <p className="text-sm text-zinc-400 py-6">
                    まだ書類がありません — ファイルをこの画面にドロップすると、AIが内容を読み取ります
                  </p>
                ) : (
                  <div className="grid gap-6 [grid-template-columns:repeat(auto-fill,minmax(min(232px,100%),1fr))]">
                    {recent.map((d) => <DocCard key={d.id} doc={d} small onOpen={setViewerDocId} />)}
                  </div>
                )}
              </section>
            </div>
          ) : (
            <>
              {view === 'browse' && currentFolder && (
                <FolderBar folder={currentFolder} folders={folders} docCount={docs.length}
                  canEdit={meta.can_edit} canManage={meta.can_manage} isAdmin={meta.is_admin}
                  pinned={prefs.pinned_folder_ids}
                  onOpenFolder={openFolder}
                  onAddFiles={() => fileInputRef.current?.click()}
                  onNewFolder={(pid) => setFolderDlg({ parentId: pid })}
                  onEditFolder={(f, m) => setFolderDlg({ edit: f, mode: m })}
                  onChanged={refreshAll} />
              )}

              {view === 'browse' && folderId && tree.children(folderId).length > 0 && (
                <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dms-subfolders">
                  {tree.children(folderId).map((f) => (
                    <button key={f.id} type="button" onClick={() => openFolder(f.id)}
                      data-testid={`dms-subfolder-${f.seed_key || f.id}`}
                      className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-left hover:border-teal-300 hover:shadow-sm transition-[box-shadow,border-color] duration-200">
                      <Folder size={17} weight="duotone" className="text-zinc-400 shrink-0" />
                      <span className="text-sm text-zinc-800 truncate flex-1">{f.name}</span>
                      <span className="text-sm text-zinc-400 tabular-nums"
                        title={`直下 ${f.document_count || 0}件`}>
                        {f.document_count_total ?? f.document_count ?? 0}
                        {(f.document_count_total ?? 0) > (f.document_count ?? 0) ? '（サブ含む）' : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {selectedIds.length > 0 && meta.can_edit && view !== 'trash' && (
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2"
                  data-testid="dms-bulk-bar">
                  <span className="text-sm font-semibold text-teal-900 tabular-nums">{selectedIds.length}件 選択中</span>
                  <Button size="sm" variant="outline" className="h-8 text-xs bg-white"
                    onClick={() => setBulkMode('move')} data-testid="dms-bulk-move">移動</Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs bg-white"
                    onClick={() => setBulkMode('type')} data-testid="dms-bulk-type">種類変更</Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs bg-white text-rose-600 border-rose-200"
                    data-testid="dms-bulk-trash"
                    onClick={() => setConfirm({
                      title: 'ゴミ箱へ移動',
                      message: `選択した ${selectedIds.length}件 をゴミ箱へ移動します。\nゴミ箱からは保持期間内であれば戻せます。`,
                      confirmLabel: 'ゴミ箱へ移動', tone: 'danger',
                      onConfirm: () => { setConfirm(null); runBulk('trash'); },
                    })}>ゴミ箱へ</Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs bg-white"
                    onClick={() => setShareFor({ ids: selectedIds })} data-testid="dms-bulk-share">
                    共有
                  </Button>
                  <button type="button" onClick={() => setSelected({})} data-testid="dms-bulk-clear"
                    className="ml-auto text-xs text-teal-800 underline">選択を解除</button>
                </div>
              )}
              {bulkMode === 'move' && (
                <div className="mb-3 rounded-lg border border-zinc-200 bg-white p-3" data-testid="dms-bulk-move-panel">
                  <p className="text-sm font-semibold text-zinc-700 mb-1.5">移動先のフォルダーを選んでください</p>
                  <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                    {folders.filter((f) => !f.is_archive_folder).map((f) => (
                      <button key={f.id} type="button" data-testid={`dms-bulk-move-to-${f.id}`}
                        onClick={() => runBulk('move', { folder_id: f.id })}
                        className="rounded-full border border-zinc-200 px-2.5 py-1 text-xs text-zinc-700 hover:bg-teal-50">
                        {f.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {bulkMode === 'type' && (
                <div className="mb-3 rounded-lg border border-zinc-200 bg-white p-3" data-testid="dms-bulk-type-panel">
                  <p className="text-sm font-semibold text-zinc-700 mb-1.5">変更後の種類を選んでください</p>
                  <div className="flex flex-wrap gap-1.5">
                    {meta.doc_types.map((t) => (
                      <button key={t.value} type="button" data-testid={`dms-bulk-type-${t.value}`}
                        onClick={() => runBulk('type', { doc_type: t.value })}
                        className="rounded-full border border-zinc-200 px-2.5 py-1 text-xs text-zinc-700 hover:bg-teal-50">
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {loading ? (
                <div className="py-16 text-center" data-testid="dms-list-loading">
                  <p className="text-sm text-zinc-400">{q ? '検索中…' : '読み込み中...'}</p>
                  {slow && (
                    <p className="mt-2 text-sm text-amber-700" data-testid="dms-search-slow">
                      時間がかかっています — 条件を絞ってください
                    </p>
                  )}
                </div>
              ) : docs.length === 0 ? (
                <div className="py-16 text-center text-zinc-400" data-testid="dms-empty">
                  <Files size={40} weight="duotone" className="mx-auto mb-2" />
                  <p className="text-sm">
                    {view === 'trash' ? 'ゴミ箱は空です'
                      : q ? '見つかりませんでした — 別の言葉でもう一度お試しください'
                        : 'まだ書類がありません — ファイルをここにドロップすると、AIが内容を読み取ります'}
                  </p>
                </div>
              ) : (
                <>
                {view === 'trash' && _isAdmin && (
                  <TrashDangerZone onEmptied={() => window.location.reload()} />
                )}
                {mode === 'list' ? (
                <><DocList /><MoreButton /></>
                ) : mode === 'split' && view === 'browse' ? (
                <div className="flex gap-4" data-testid="dms-split-view">
                  <div className="flex-1 min-w-0">
                  <div className="grid gap-4 content-start [grid-template-columns:repeat(auto-fill,minmax(min(232px,100%),1fr))]">
                    {visibleDocs.map((d) => (
                      <DocCard key={d.id} doc={d} small onOpen={setViewerDocId}
                        selectable selected={!!selected[d.id]}
                        onSelect={(v) => setSelected((p) => ({ ...p, [d.id]: v }))} />
                    ))}
                  </div>
                  <MoreButton />
                  </div>
                  <div className="w-56 shrink-0 rounded-lg border border-zinc-200 bg-white p-3" data-testid="dms-split-target">
                    <p className="text-sm font-semibold text-zinc-700 mb-1">選択した書類の移動先</p>
                    <p className="text-xs text-zinc-400 mb-2">{Object.values(selected).filter(Boolean).length}件 選択中</p>
                    {meta.features?.bundles_binders_envelopes && meta.can_edit && (
                      <button type="button"
                        onClick={() => {
                          const ids = Object.keys(selected).filter((k) => selected[k]);
                          if (ids.length < 2) { toast.error('2件以上選択してください'); return; }
                          setBundlePick(ids);
                        }}
                        className="w-full mb-2 flex items-center justify-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium py-1.5 hover:bg-amber-100 transition-colors duration-200"
                        data-testid="dms-bundle-selected-btn">
                        <Books size={13} weight="duotone" /> 束ねる（バインダー）
                      </button>
                    )}
                    <Input value={folderQ} onChange={(e) => setFolderQ(e.target.value)}
                      placeholder="フォルダーを検索" className="h-8 mb-1.5 text-sm"
                      data-testid="dms-folder-picker-search" />
                    <div className="space-y-1 max-h-[420px] overflow-y-auto">
                      {folders.filter((f) => !f.is_archive_folder)
                        .filter((f) => !folderQ.trim() || (f.name || '').toLowerCase().includes(folderQ.trim().toLowerCase()))
                        .map((f) => (
                          <button key={f.id} type="button" onClick={() => moveSelected(f.id)}
                            data-testid={`dms-move-to-${f.seed_key || f.id}`}
                            className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm text-zinc-600 hover:bg-teal-50 hover:text-teal-800 text-left">
                            <Folder size={13} weight="duotone" className="text-zinc-400 shrink-0" />
                            <span className="truncate">{f.name}</span>
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                <div className={`grid ${cardSize === 'sm' ? 'gap-4' : 'gap-6'}`}
                  style={{ gridTemplateColumns: `repeat(auto-fill,minmax(min(${
                    { sm: 168, md: 232, lg: 320 }[cardSize]}px,100%),1fr))` }}
                  data-testid="dms-doc-grid">
                  {visibleDocs.map((d) => (
                    <DocCard key={d.id} doc={d} onOpen={setViewerDocId} size={cardSize}
                      selectable selected={!!selected[d.id]}
                      onSelect={(v) => setSelected((p) => ({ ...p, [d.id]: v }))}
                      actions={cardActions(d)} />
                  ))}
                </div>
                <MoreButton />
                </>
              )}
                </>
              )}
            </>
          )}
        </main>
      </div>

      {uploadOpen && (
        <DocUploadDialog meta={meta} folders={folders} defaultFolderId={folderId}
          onClose={() => setUploadOpen(false)}
          onSaved={() => { setUploadOpen(false); refreshAll(); }} />
      )}
      {folderDlg && (
        <FolderDialog folders={folders} editFolder={folderDlg.edit} mode={folderDlg.mode}
          defaultParentId={folderDlg.parentId}
          onClose={() => setFolderDlg(null)}
          onSaved={() => { setFolderDlg(null); refreshAll(); }} />
      )}
      {viewerDocId && meta?.features?.viewer_shell && (
        <DocViewer docId={viewerDocId} meta={meta} initialPage={viewerPage}
          onClose={() => { setViewerDocId(null); setBundleCtx(null); setViewerPage(null); }} onChanged={refreshAll}
          onOpenEditor={(id) => { setViewerDocId(null); setBundleCtx(null); setEditorDocId(id); }}
          onOpenDoc={(id) => setViewerDocId(id)}
          bundleNav={bundleCtx ? (() => {
            const idx = bundleCtx.ids.indexOf(viewerDocId);
            return {
              label: bundleCtx.name, index: idx, total: bundleCtx.ids.length,
              onPrev: () => idx > 0 && setViewerDocId(bundleCtx.ids[idx - 1]),
              onNext: () => idx < bundleCtx.ids.length - 1 && setViewerDocId(bundleCtx.ids[idx + 1]),
            };
          })() : null} />
      )}
      {viewerDocId && meta && !meta.features?.viewer_shell && (
        <DocDetailDrawer docId={viewerDocId} meta={meta} folders={folders}
          onClose={() => setViewerDocId(null)} onChanged={refreshAll} />
      )}
      {editorDocId && (
        <DocDetailDrawer docId={editorDocId} meta={meta} folders={folders} editOnly
          onClose={() => setEditorDocId(null)} onChanged={refreshAll} />
      )}
      <CreateBundleDialog open={!!bundlePick} documentIds={bundlePick || []}
        onClose={() => setBundlePick(null)}
        onCreated={() => { setBundlePick(null); setSelected({}); setView('bundles'); }} />
      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        confirmLabel={confirm?.confirmLabel} tone={confirm?.tone} testid="dms-confirm"
        onCancel={() => setConfirm(null)} onConfirm={confirm?.onConfirm} />
      <DocReadDialog open={!!readDlg} documentId={readDlg?.id} title={readDlg?.title}
        canEdit={!!meta.can_edit && !readDlg?.read_only && !readDlg?.is_deleted}
        onClose={() => setReadDlg(null)} onChanged={refreshAll} />
      <AskScopeDialog open={askScopeOpen} onClose={() => setAskScopeOpen(false)}
        folders={folders} onOpenDoc={(id) => { setAskScopeOpen(false); setViewerDocId(id); }} />
      {scanDlg && (
        <BarcodeScanDialog open={scanDlg} onClose={() => setScanDlg(false)}
          onResolved={(target) => { setScanDlg(false); setAssetTarget(target); setView('assetdocs'); }} />
      )}

      {/* 設定・管理 — 管理者向けの三次アクションはすべてこの右ドロワーに集約 */}
      {adminOpen && meta.is_admin && (
        <>
          <div className="fixed inset-0 z-40 bg-zinc-900/25" onClick={() => setAdminOpen(false)} aria-hidden />
          <aside role="dialog" aria-modal="true" aria-label="設定・管理"
            onKeyDown={(e) => { if (e.key === 'Escape') setAdminOpen(false); }}
            className="fixed right-0 top-14 sm:top-16 z-50 h-[calc(100%-3.5rem)] sm:h-[calc(100%-4rem)] w-full sm:w-[600px] bg-white border-l border-zinc-200 shadow-xl flex flex-col"
            data-testid="dms-admin-drawer">
            <header className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200">
              <GearSix size={17} weight="duotone" className="text-zinc-500" />
              <div>
                <p className="text-base font-medium text-zinc-900 leading-tight">設定・管理</p>
                <p className="text-sm text-zinc-500">一括処理・検索インデックス・ファイル統合・機能設定（管理者のみ）</p>
              </div>
              <button type="button" onClick={() => setAdminOpen(false)} data-testid="dms-admin-drawer-close"
                className="ml-auto p-1.5 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors duration-200">
                <X size={16} />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <DocsHealthCard />
              <DmsAdminPanel />
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
